import { useCallback, useState } from 'preact/hooks';
import { MicCheck } from '../components/MicCheck.js';
import { Avatar } from '../components/Avatar.js';
import { haptic } from '../lib/haptics.js';
import type { PublicRoom } from '../lib/api.js';

/**
 * The doorway.
 *
 * A separate step rather than joining straight from the link, and it earns that
 * tap three times over: a name before anyone hears you, a microphone proven
 * working BEFORE the room rather than during it, and a moment to see what you
 * are walking into. Every one of those is worse discovered live.
 */
export function PreJoin({
  room,
  presence,
  mySub,
  onJoin,
  connecting,
  error,
  notice,
}: {
  room: PublicRoom;
  presence: { listening: number; live: boolean } | null;
  mySub: string;
  onJoin: (name: string) => void;
  connecting: boolean;
  error: string | null;
  notice?: string | null;
}) {
  const [name, setName] = useState(() => localStorage.getItem('waves.name') ?? '');
  const [, setMicReady] = useState(false);
  const trimmed = name.trim();
  const valid = trimmed.length >= 2 && trimmed.length <= 40;

  const handleReady = useCallback((ok: boolean) => setMicReady(ok), []);

  return (
    <main class="door">
      <div class="door__card">
        <div class="door__eyebrow">
          {presence?.live ? (
            <span class="chip chip--live">
              <span class="chip__dot" />
              Live now
            </span>
          ) : (
            <span class="chip">You're invited</span>
          )}
        </div>

        <h1 class="door__title">{room.title}</h1>

        <p class="door__meta">
          {presence && presence.listening > 0
            ? `${presence.listening.toLocaleString()} listening`
            : 'Be the first one in'}
        </p>

        {notice ? <p class="notice notice--good">{notice}</p> : null}

        <form
          class="door__form"
          onSubmit={(event) => {
            event.preventDefault();
            if (!valid || connecting) return;
            haptic('press');
            localStorage.setItem('waves.name', trimmed);
            onJoin(trimmed);
          }}
        >
          <div class="door__identity">
            {/* Their own avatar, live as they type — the room is about people,
                so the first thing they should see is themselves in it. */}
            <Avatar sub={mySub} name={trimmed || '?'} size="md" />
            <div class="door__field">
              <label for="name">Your name</label>
              <input
                id="name"
                value={name}
                placeholder="Kwame"
                autocomplete="name"
                maxLength={40}
                enterkeyhint="go"
                onInput={(event) => setName((event.target as HTMLInputElement).value)}
              />
            </div>
          </div>

          <MicCheck onReady={handleReady} active={!connecting} />

          {error ? <p class="notice notice--bad">{error}</p> : null}

          <button class="door__join" type="submit" disabled={!valid || connecting}>
            {/* Never a spinner void — say what is happening, in words. */}
            {connecting ? 'Joining…' : 'Join the room'}
          </button>
        </form>

        <p class="door__fine">
          You'll join muted and listening. Anyone can pick any name — here a name
          is a label, never a permission.
        </p>
      </div>
    </main>
  );
}
